// Single source of truth for "who am I". Used by App.tsx for routing
// gates and by the editor for displaying the current user.

import { useCallback, useEffect, useState } from 'react';
import { authApi, ApiError, type PasskeySummary } from '../lib/api';
import type { User } from '@shared/types';

interface State {
  loading: boolean;
  user: User | null;
  passkeys: PasskeySummary[];
}

export function useCurrentUser() {
  const [state, setState] = useState<State>({ loading: true, user: null, passkeys: [] });

  const refresh = useCallback(async () => {
    try {
      const { user, passkeys } = await authApi.me();
      setState({ loading: false, user, passkeys });
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        setState({ loading: false, user: null, passkeys: [] });
      } else {
        // Network error etc — treat as logged out so we don't lock the UI
        // on a transient failure. The user will see the login page and
        // any deeper error will surface there.
        setState({ loading: false, user: null, passkeys: [] });
      }
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {}
    setState({ loading: false, user: null, passkeys: [] });
  }, []);

  return { ...state, refresh, logout };
}
